import { Container, Row, Col } from 'react-bootstrap';
import Link from 'next/link';
import { useNavigate, useLocation } from '@/compat/react-router-dom';
import { useHomePage, getHomeSectionByKey, iconMap } from '@/hooks/useHomePage';
import { scrollToHash } from '@/utils/scrollToHash';

const HeroIconBoxes = ({ sectionData: propSectionData, disableAutoFetch = false }) => {
  const shouldFetch = !propSectionData && !disableAutoFetch;
  const { sections } = useHomePage(null, shouldFetch);
  const heroSection = propSectionData || getHomeSectionByKey(sections, 'hero');
  const navigate = useNavigate();
  const location = useLocation();
  
  if (!heroSection) return null;
  
  const iconItems = (heroSection.items || [])
    .filter((item) => {
      const isActive = item?.is_active === 1 || item?.is_active === '1' || item?.is_active === true;
      return item?.item_type === 'icon' && isActive;
    })
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  if (iconItems.length === 0) return null;

  const handleLinkClick = (e, link) => {
    if (!link) return;
    
    // Check if link has a hash anchor
    const hashIndex = link.indexOf('#');
    const hasHash = hashIndex !== -1;
    
    if (hasHash) {
      const path = hashIndex > 0 ? link.substring(0, hashIndex) : '/';
      const hash = link.substring(hashIndex);
      const targetPath = path || '/';
      
      // If we're already on the target page, prevent default and scroll
      if (location.pathname === targetPath) {
        e.preventDefault();
        // Update URL hash
        window.history.pushState(null, '', link);
        scrollToHash(hash, 100);
      } else {
        // For cross-page navigation, navigate first then scroll
        e.preventDefault();
        // Navigate to path first
        navigate(path);
        // Set hash in URL after navigation
        setTimeout(() => {
          window.history.replaceState(null, '', link);
          scrollToHash(hash, 100, 10);
        }, 100);
      }
    }
    // If no hash, let Link handle it normally (no preventDefault)
  };

  return (
    <div className="hero-icon-boxes">
      <Container>
        <Row>
          {iconItems.map((item, idx) => {
            const Icon = item.icon ? iconMap[item.icon] : null;
            const validColors = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark'];
            const colorClass = (item.icon_color && validColors.includes(String(item.icon_color).toLowerCase()))
              ? String(item.icon_color).toLowerCase()
              : 'primary';
            const link = item.link || item.content?.link || null;
            const hasHash = link && link.includes('#');
            
            const BoxContent = () => (
              <>
                {Icon && (
                  <div className={`hero-icon-box__icon hero-icon-box__icon--${colorClass}`}>
                    <Icon size={36} />
                  </div>
                )}
                {item.title && (
                  <h5 className="hero-icon-box__title">{item.title}</h5>
                )}
                {item.description && (
                  <p className="hero-icon-box__desc" dangerouslySetInnerHTML={{ __html: item.description }} />
                )}
              </>
            );

            if (link) {
              return (
                <Col key={item.id || idx} xs={12} sm={6} md={4} lg className="hero-icon-box-col">
                  {hasHash ? (
                    <Link 
                      to={link}
                      className="hero-icon-box"
                      onClick={(e) => handleLinkClick(e, link)}
                    >
                      <BoxContent />
                    </Link>
                  ) : (
                    <Link href={link} className="hero-icon-box">
                      <BoxContent />
                    </Link>
                  )}
                </Col>
              );
            }

            return (
              <Col key={item.id || idx} xs={12} sm={6} md={4} lg className="hero-icon-box-col">
                <div className="hero-icon-box">
                  <BoxContent />
                </div>
              </Col>
            );
          })}
        </Row>
      </Container>
    </div>
  );
};

export default HeroIconBoxes;
